import { useState } from 'react';
import { ChevronDown, ChevronRight, X, Search } from 'lucide-react';
import { SKILL_CATEGORIES, OUTCOME_CATEGORIES } from '../../../constants/skills-outcomes';

interface SkillsOutcomesSelectorProps {
  selectedSkills: string[];
  selectedOutcomes: string[];
  onSkillsChange: (skills: string[]) => void;
  onOutcomesChange: (outcomes: string[]) => void;
}

const SkillsOutcomesSelector = ({
  selectedSkills,
  selectedOutcomes,
  onSkillsChange,
  onOutcomesChange,
}: SkillsOutcomesSelectorProps) => {
  const [skillsExpanded, setSkillsExpanded] = useState<Record<string, boolean>>({});
  const [outcomesExpanded, setOutcomesExpanded] = useState<Record<string, boolean>>({});
  const [skillSearch, setSkillSearch] = useState('');
  const [outcomeSearch, setOutcomeSearch] = useState('');

  const toggleSkill = (skill: string) => {
    if (selectedSkills.includes(skill)) {
      onSkillsChange(selectedSkills.filter((s) => s !== skill));
    } else {
      onSkillsChange([...selectedSkills, skill]);
    }
  };

  const toggleOutcome = (outcome: string) => {
    if (selectedOutcomes.includes(outcome)) {
      onOutcomesChange(selectedOutcomes.filter((o) => o !== outcome));
    } else {
      onOutcomesChange([...selectedOutcomes, outcome]);
    }
  };

  const toggleSkillCategory = (category: string) => {
    setSkillsExpanded((prev) => ({ ...prev, [category]: !prev[category] }));
  };

  const toggleOutcomeCategory = (category: string) => {
    setOutcomesExpanded((prev) => ({ ...prev, [category]: !prev[category] }));
  };

  // Filter skills by search
  const filteredSkillCategories = Object.entries(SKILL_CATEGORIES).reduce(
    (acc, [category, skills]) => {
      const filtered = skills.filter((skill) =>
        skill.toLowerCase().includes(skillSearch.toLowerCase())
      );
      if (filtered.length > 0) {
        acc[category] = filtered;
      }
      return acc;
    },
    {} as Record<string, string[]>
  );

  // Filter outcomes by search
  const filteredOutcomeCategories = Object.entries(OUTCOME_CATEGORIES).reduce(
    (acc, [category, outcomes]) => {
      const filtered = outcomes.filter((outcome) =>
        outcome.toLowerCase().includes(outcomeSearch.toLowerCase())
      );
      if (filtered.length > 0) {
        acc[category] = filtered;
      }
      return acc;
    },
    {} as Record<string, string[]>
  );

  return (
    <div className="grid grid-cols-2 gap-6">
      {/* Skills Column */}
      <div>
        <h3 className="text-sm font-medium text-[#020817] mb-3">Skills</h3>

        {/* Selected Skills */}
        {selectedSkills.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {selectedSkills.map((skill) => (
              <span
                key={skill}
                className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs"
              >
                {skill}
                <button
                  type="button"
                  onClick={() => toggleSkill(skill)}
                  className="hover:text-blue-900"
                >
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
        )}

        {/* Search */}
        <div className="relative mb-3">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search skills..."
            value={skillSearch}
            onChange={(e) => setSkillSearch(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 text-xs border border-[#E2E8F0] rounded-lg focus:outline-none focus:border-[#020817]"
          />
        </div>

        {/* Categories */}
        <div className="max-h-[300px] overflow-y-auto border border-[#E2E8F0] rounded-lg">
          {Object.entries(filteredSkillCategories).map(([category, skills]) => (
            <div key={category} className="border-b border-[#E2E8F0] last:border-b-0">
              <button
                type="button"
                onClick={() => toggleSkillCategory(category)}
                className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium text-[#020817] hover:bg-gray-50"
              >
                {skillsExpanded[category] ? (
                  <ChevronDown size={14} />
                ) : (
                  <ChevronRight size={14} />
                )}
                {category}
                <span className="ml-auto text-gray-400">
                  {skills.filter((s) => selectedSkills.includes(s)).length}/{skills.length}
                </span>
              </button>
              {skillsExpanded[category] && (
                <div className="px-3 pb-2 space-y-1">
                  {skills.map((skill) => (
                    <label
                      key={skill}
                      className="flex items-center gap-2 py-1 px-2 rounded hover:bg-gray-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedSkills.includes(skill)}
                        onChange={() => toggleSkill(skill)}
                        className="accent-[#020817] w-3.5 h-3.5"
                      />
                      <span className="text-xs text-gray-700">{skill}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Outcomes Column */}
      <div>
        <h3 className="text-sm font-medium text-[#020817] mb-3">Learning Outcomes</h3>

        {/* Selected Outcomes */}
        {selectedOutcomes.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {selectedOutcomes.slice(0, 3).map((outcome) => (
              <span
                key={outcome}
                className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs max-w-[200px] truncate"
                title={outcome}
              >
                {outcome.length > 30 ? outcome.slice(0, 30) + '...' : outcome}
                <button
                  type="button"
                  onClick={() => toggleOutcome(outcome)}
                  className="hover:text-purple-900 flex-shrink-0"
                >
                  <X size={12} />
                </button>
              </span>
            ))}
            {selectedOutcomes.length > 3 && (
              <span className="text-xs text-gray-500">+{selectedOutcomes.length - 3} more</span>
            )}
          </div>
        )}

        {/* Search */}
        <div className="relative mb-3">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search outcomes..."
            value={outcomeSearch}
            onChange={(e) => setOutcomeSearch(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 text-xs border border-[#E2E8F0] rounded-lg focus:outline-none focus:border-[#020817]"
          />
        </div>

        {/* Categories */}
        <div className="max-h-[300px] overflow-y-auto border border-[#E2E8F0] rounded-lg">
          {Object.entries(filteredOutcomeCategories).map(([category, outcomes]) => (
            <div key={category} className="border-b border-[#E2E8F0] last:border-b-0">
              <button
                type="button"
                onClick={() => toggleOutcomeCategory(category)}
                className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium text-[#020817] hover:bg-gray-50"
              >
                {outcomesExpanded[category] ? (
                  <ChevronDown size={14} />
                ) : (
                  <ChevronRight size={14} />
                )}
                {category}
                <span className="ml-auto text-gray-400">
                  {outcomes.filter((o) => selectedOutcomes.includes(o)).length}/{outcomes.length}
                </span>
              </button>
              {outcomesExpanded[category] && (
                <div className="px-3 pb-2 space-y-1">
                  {outcomes.map((outcome) => (
                    <label
                      key={outcome}
                      className="flex items-start gap-2 py-1 px-2 rounded hover:bg-gray-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedOutcomes.includes(outcome)}
                        onChange={() => toggleOutcome(outcome)}
                        className="accent-[#020817] w-3.5 h-3.5 mt-0.5 flex-shrink-0"
                      />
                      <span className="text-xs text-gray-700">{outcome}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SkillsOutcomesSelector;
